import { useEffect, useRef, useState } from "react";
import {
  ArrowDownUp,
  ArrowLeft,
  ArrowRight,
  Bell,
  Bookmark,
  Check,
  ChevronDown,
  ChevronRight,
  Compass,
  Cookie,
  ExternalLink,
  Flag,
  Heart,
  HelpCircle,
  Leaf,
  LocateFixed,
  MapPin,
  Menu,
  Navigation,
  Plus,
  Search,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  UserRound,
  X,
} from "lucide-react";
import {
  buddyStage,
  buddyStages,
  defaultProductIds,
  defaultCategoryIds,
  products as initialProducts,
  type Cluster,
  type Dashboard,
  type Evidence,
  type Product,
  type SearchFilters,
  type SearchResult,
  type StoreMatch,
  type SavedSearch,
} from "@little-finds/core";
import { api, demo, configured, rpc, supabase } from "./api";
import { demoFilters, demoStores, readDemo, writeDemo } from "./demo";
import { draftKeyFor, saveDraftPhoto } from "./sightingDraft";
import { useEvidencePhotos } from "./useEvidencePhotos";
import {
  defaultFilters,
  filtersKey,
  parseFilters,
  pendingGuestFiltersKey,
  loginFilterSelection,
} from "./filterState";
import { evidenceAge as age, representativeMatch } from "./evidenceView";
import { Buddy } from "./components/Buddy";
import { ProductArt } from "./components/ProductArt";
import { MapCanvas } from "./components/MapCanvas";
import { ResultsBrowser } from "./components/ResultsBrowser";
import { clusterSearchFilters } from "./clusterNavigation";
import { LocationSearch } from "./components/LocationSearch";
import { DirectoryBrowser } from "./components/DirectoryBrowser";
import {
  anyCharmCategory,
  chooseCharmMode,
  isAllProducts,
  productIsSelected,
  searchAreaSummary,
  setWishlistVariant,
  toggleWishlistProduct,
  wishlistSummary,
} from "./wishlist";
import { AdminPanel } from "./components/AdminPanel";
import { Modal } from "./components/Modal";
import {
  LoginForm,
  SaveSearchForm,
  SightingForm,
  SuggestStoreForm,
} from "./components/Forms";
const empty: SearchResult = {
  stores: [],
  clusters: [],
  total_matches: 0,
  recent_stores: 0,
  generated_at: "",
  has_more: false,
};
const staging = import.meta.env.VITE_APP_ENV === "staging";
const blankDashboard: Dashboard = {
  buddy: { available_treats: 0, total_fed: 0, pending_treats: 0 },
  saved_searches: [],
  notifications: [],
  is_admin: false,
  pending_visits: 0,
};
function storedFilters(owner?: string | null): SearchFilters | undefined {
  try {
    return parseFilters(localStorage.getItem(filtersKey(owner)));
  } catch {
    return undefined;
  }
}
function readFilters(owner?: string | null): SearchFilters {
  return demo ? { ...demoFilters } : (storedFilters(owner) ?? defaultFilters());
}
const areaLabel = (filters: SearchFilters) =>
  filters.bounds
    ? "Your map area"
    : filters.scope === "us"
      ? "United States"
      : "Your saved search area";
const titleFor = (state: string) =>
  ({
    recent: "Seen recently",
    older: "Older sighting",
    mention: "Public mention",
    stockist: "Carries the brand",
    conflicting: "Mixed reports",
  })[state] ?? state;
export default function App() {
  const [filters, setFilters] = useState<SearchFilters>(readFilters),
    [catalog, setCatalog] = useState<Product[]>(initialProducts),
    [result, setResult] = useState<SearchResult>(empty),
    [selected, setSelected] = useState<StoreMatch | null>(null),
    [history, setHistory] = useState<Evidence[]>([]),
    [historyError, setHistoryError] = useState(""),
    [historyLoading, setHistoryLoading] = useState(false),
    [historyRefresh, setHistoryRefresh] = useState(0),
    [dashboard, setDashboard] = useState(blankDashboard),
    [user, setUser] = useState<string | null>(demo ? "Demo collector" : null),
    [userId, setUserId] = useState<string | null>(
      demo ? "demo-collector" : null,
    ),
    [view, setView] = useState("map"),
    [modal, setModal] = useState<string | null>(null),
    [toast, setToast] = useState(""),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [area, setArea] = useState(demo ? "Chicago, IL" : areaLabel(readFilters())),
    [brand, setBrand] = useState("All"),
    [productQuery, setProductQuery] = useState(""),
    [showFilters, setShowFilters] = useState(false),
    [sort, setSort] = useState("distance"),
    [feeding, setFeeding] = useState(false),
    [adminData, setAdminData] = useState<Record<string, any> | null>(null),
    [reportId, setReportId] = useState(""),
    [reportReason, setReportReason] = useState(""),
    [variantProduct, setVariantProduct] = useState<string | null>(null),
    [variantText, setVariantText] = useState(""),
    [loadingMore, setLoadingMore] = useState(false),
    [savingHunt, setSavingHunt] = useState<string | null>(null),
    [activeHunt, setActiveHunt] = useState<SavedSearch | undefined>(),
    [editingHunt, setEditingHunt] = useState<SavedSearch | undefined>();
  const filterPanel = useRef<HTMLElement>(null);
  const detailHeading = useRef<HTMLHeadingElement>(null),
    resultsHeading = useRef<HTMLHeadingElement>(null);
  const previousStore = useRef<string | null>(null),
    returnStore = useRef<string | null>(null),
    restoreStoreFocus = useRef(false);
  const searchSequence = useRef(0),
    afterLogin = useRef<string | null>(null),
    currentUser = useRef(userId),
    feedRequest = useRef<string | null>(null),
    directStore = useRef<string | null>(null),
    ownerStoredFilters = useRef<SearchFilters | undefined>(undefined);
  currentUser.current = userId;
  function notify(s: string) {
    setToast(s);
  }
  function changeFilters(
    next: React.SetStateAction<SearchFilters>,
    rememberGuest = true,
  ) {
    setFilters((previous) => {
      const updated = typeof next === "function" ? next(previous) : next;
      if (!demo && !currentUser.current) {
        try {
          if (rememberGuest || localStorage.getItem(pendingGuestFiltersKey))
            localStorage.setItem(
              pendingGuestFiltersKey,
              JSON.stringify(updated),
            );
        } catch {
          /* Browsing still works without local storage. */
        }
      }
      return updated;
    });
  }

  function exploreCluster(cluster: Cluster) {
    clearSelectedStore();
    setArea("Your selected area");
    changeFilters((previous) => clusterSearchFilters(previous, cluster), false);
    resultsHeading.current?.focus({ preventScroll: true });
  }

  async function refresh() {
    const seq = ++searchSequence.current;
    setLoading(true);
    setError("");
    try {
      const r = await api.search(filters);
      if (seq === searchSequence.current) {
        setResult(r);
        setSelected((s) =>
          s
            ? (r.stores.find((v) => v.id === s.id) ??
              (s.id === directStore.current ? s : null))
            : null,
        );
      }
    } catch (e) {
      if (seq === searchSequence.current) {
        setError((e as Error).message);
        setResult(empty);
      }
    } finally {
      if (seq === searchSequence.current) setLoading(false);
    }
  }
  async function refreshAdmin() {
    const owner = currentUser.current;
    if (!owner) return;
    const d = await api.admin();
    if (currentUser.current === owner) setAdminData(d);
  }
  async function signOut() {
    currentUser.current = null;
    setUserId(null);
    setUser(null);
    setActiveHunt(undefined);
    setEditingHunt(undefined);
    setFilters(readFilters());
    setDashboard(blankDashboard);
    setAdminData(null);
    setHistory([]);
    setView("map");
    setModal(null);
    if (demo) {
      location.href = "/";
      return;
    }
    try {
      const { error } = await supabase!.auth.signOut({ scope: "local" });
      if (error) notify(error.message);
    } catch (e) {
      notify((e as Error).message);
    }
  }
  async function refreshDashboard() {
    const owner = currentUser.current;
    if (!owner) return;
    try {
      const d = await api.dashboard();
      if (currentUser.current === owner) setDashboard(d);
    } catch (e) {
      if (currentUser.current === owner) notify((e as Error).message);
    }
  }
  useEffect(() => {
    api
      .catalog()
      .then(setCatalog)
      .catch(() => {});
    if (!supabase || demo) return;
    const acceptSession = (
      session: { user: { id: string; email?: string } } | null,
    ) => {
      const owner = session?.user.id ?? null;
      if (currentUser.current !== owner) {
        if (owner && session?.user.email) {
          try {
            const previousKey = draftKeyFor(session.user.email),
              nextKey = draftKeyFor(owner);
            const previous = localStorage.getItem(previousKey);
            if (previous && !localStorage.getItem(nextKey))
              localStorage.setItem(nextKey, previous);
            localStorage.removeItem(previousKey);
          } catch {
            /* Drafts remain available when storage permits. */
          }
        }
        currentUser.current = owner;
        feedRequest.current = null;
        setFeeding(false);
        setDashboard(blankDashboard);
        setAdminData(null);
        setHistory([]);
        setActiveHunt(undefined);
        setEditingHunt(undefined);
        ownerStoredFilters.current = storedFilters(owner);
        setFilters(ownerStoredFilters.current ?? defaultFilters());
        setArea(areaLabel(ownerStoredFilters.current ?? defaultFilters()));
      }
      setUserId(owner);
      setUser(session?.user.email ?? (owner ? "Collector" : null));
    };
    let alive = true,
      authVersion = 0;
    supabase.auth.getSession().then(({ data }) => {
      if (alive && authVersion === 0) acceptSession(data.session);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      authVersion += 1;
      acceptSession(session);
    });
    return () => {
      alive = false;
      subscription.unsubscribe();
    };
  }, []);
  useEffect(() => {
    void refresh();
    if (!demo) {
      try {
        localStorage.setItem(
          filtersKey(currentUser.current),
          JSON.stringify(filters),
        );
      } catch {
        /* Browsing still works with browser storage disabled. */
      }
    }
  }, [filters]);
  useEffect(() => {
    setDashboard(blankDashboard);
    setAdminData(null);
    setHistory([]);
    let alive = true;
    const owner = userId;
    if (owner) {
      void (async () => {
        try {
          let d = await api.dashboard();
          if (!alive || currentUser.current !== owner) return;
          if (!demo) {
            try {
              let pending: SearchFilters | undefined;
              try {
                pending = parseFilters(
                  localStorage.getItem(pendingGuestFiltersKey),
                );
              } catch {
                /* Use the account's saved filters when device storage is unavailable. */
              }
              const selection = loginFilterSelection(
                pending,
                ownerStoredFilters.current,
                d.saved_searches,
              );
              if (selection.save) {
                const saved = await api.save(selection.save);
                if (!alive || currentUser.current !== owner) return;
                d = {
                  ...d,
                  saved_searches: [
                    ...d.saved_searches.filter((s) => s.id !== saved.id),
                    saved,
                  ],
                };
                setFilters(saved.filters);
                try {
                  localStorage.removeItem(pendingGuestFiltersKey);
                } catch {
                  /* The merge is idempotent if retried. */
                }
              } else setFilters(selection.filters);
              setArea(areaLabel(selection.filters));
            } catch (e) {
              if (alive && currentUser.current === owner)
                notify(
                  `Your account is signed in, but the device wishlist could not sync. ${(e as Error).message}`,
                );
            }
          }
          if (alive && currentUser.current === owner) setDashboard(d);
        } catch (e) {
          if (alive) notify((e as Error).message);
        }
      })();
    }
    void refresh();
    return () => {
      alive = false;
    };
  }, [userId]);
  useEffect(() => {
    if (!showFilters) return;
    const panel = filterPanel.current;
    if (!panel) return;
    const previous = document.activeElement as HTMLElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panel.querySelector<HTMLElement>("button")?.focus();
    const trap = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowFilters(false);
        return;
      }
      if (event.key !== "Tab") return;
      const all = [
        ...panel.querySelectorAll<HTMLElement>("button,input,select,summary"),
      ].filter(
        (e) => e.getClientRects().length > 0 && !e.hasAttribute("disabled"),
      );
      if (!all.length) return;
      const first = all[0],
        last = all[all.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    panel.addEventListener("keydown", trap);
    return () => {
      panel.removeEventListener("keydown", trap);
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [showFilters]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 6500);
    return () => clearTimeout(t);
  }, [toast]);
  useEffect(() => {
    setHistory([]);
    setHistoryError("");
    setHistoryLoading(Boolean(selected));
    if (!selected) return;
    let alive = true;
    api
      .evidence(selected.id)
      .then((v) => {
        if (alive) setHistory(v);
      })
      .catch((e) => {
        if (alive) setHistoryError((e as Error).message);
      })
      .finally(() => {
        if (alive) setHistoryLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [selected?.id, result.generated_at, userId, historyRefresh]);
  useEffect(() => {
    const refreshVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const timer = setInterval(refreshVisible, 60000);
    document.addEventListener("visibilitychange", refreshVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshVisible);
    };
  }, [filters, userId]);
  useEffect(() => {
    let alive = true;
    const owner = userId;
    if (view === "admin" && owner)
      api
        .admin()
        .then((d) => {
          if (alive && currentUser.current === owner) setAdminData(d);
        })
        .catch((e) => notify(e.message));
    return () => {
      alive = false;
    };
  }, [view, userId]);
  function needAccount(action: string) {
    if (action === "save") setEditingHunt(activeHunt);
    if (user) {
      setModal(action);
      return;
    }
    afterLogin.current = action;
    setModal("login");
  }
  async function openListedStore(id: string) {
    try {
      const store = await api.store(id);
      if (!store)
        throw new Error(
          "This shop is no longer listed. Its directory or source information may have changed.",
        );
      directStore.current = store.id;
      changeFilters((f) => ({
        ...f,
        center: { latitude: store.latitude, longitude: store.longitude },
        radius_miles: 25,
        scope: "radius",
        zoom: 11,
        offset: 0,
        bounds: undefined,
      }));
      setSelected({
        ...store,
        matches: [],
        recent_count: 0,
        distance_miles: 0,
      });
      setArea(`${store.city}, ${store.state}`);
      setView("map");
      setModal(null);
    } catch (e) {
      notify((e as Error).message);
    }
  }
  function clearSelectedStore() {
    directStore.current = null;
    restoreStoreFocus.current = false;
    setSelected(null);
  }
  function chooseProduct(id: string) {
    changeFilters((f) => toggleWishlistProduct(f, id, catalog));
  }
  function chooseStore(store: StoreMatch) {
    restoreStoreFocus.current = false;
    returnStore.current = store.id;
    setSelected(store);
  }
  useEffect(() => {
    const prior = previousStore.current;
    previousStore.current = selected?.id ?? null;
    const frame = requestAnimationFrame(() => {
      if (selected) {
        detailHeading.current?.focus({ preventScroll: true });
        if (window.matchMedia("(max-width: 1000px)").matches)
          detailHeading.current?.scrollIntoView({
            block: "start",
            behavior: "auto",
          });
      } else if (prior && restoreStoreFocus.current) {
        restoreStoreFocus.current = false;
        const button = returnStore.current
          ? document.querySelector<HTMLButtonElement>(
              `[data-store-id="${CSS.escape(returnStore.current)}"]`,
            )
          : null;
        (button ?? resultsHeading.current)?.focus({ preventScroll: true });
        if (window.matchMedia("(max-width: 1000px)").matches)
          (button ?? resultsHeading.current)?.scrollIntoView({
            block: "nearest",
            behavior: "auto",
          });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [selected?.id]);
  function toggleLayer(k: keyof SearchFilters["layers"]) {
    changeFilters((f) => ({
      ...f,
      offset: 0,
      layers: { ...f.layers, [k]: !f.layers[k] },
    }));
  }
  async function locate() {
    if (demo) {
      notify(
        "This sample hunt is set in Chicago. Exit demo to search near you.",
      );
      return;
    }
    if (!navigator.geolocation) {
      notify("Location is unavailable. Search a city or ZIP instead.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        clearSelectedStore();
        setArea("Near you");
        changeFilters((f) => ({
          ...f,
          center: {
            latitude: p.coords.latitude,
            longitude: p.coords.longitude,
          },
          radius_miles: 25,
          scope: "radius",
          zoom: 11,
          offset: 0,
          bounds: undefined,
        }));
      },
      () =>
        notify("Location wasn’t shared. You can still search by city or ZIP."),
      { enableHighAccuracy: false, timeout: 10000 },
    );
  }
  async function feed() {
    if (feeding) return;
    if (!user) {
      needAccount("buddy");
      return;
    }
    if (!dashboard.buddy.available_treats) {
      notify(
        "Your next approved visit earns a biscuit. Helpful “not found” reports count too.",
      );
      return;
    }
    const owner = currentUser.current;
    if (dashboard.buddy.total_fed >= 30) {
      notify(
        "Biscuit is happily full. Your helpful visits still make a difference.",
      );
      return;
    }
    setFeeding(true);
    try {
      const requestKey = `little-finds:feed-request:${owner}`;
      if (!feedRequest.current) {
        try {
          feedRequest.current = localStorage.getItem(requestKey);
        } catch {
          /* Keep the in-memory retry key. */
        }
        feedRequest.current ??= crypto.randomUUID();
        try {
          localStorage.setItem(requestKey, feedRequest.current);
        } catch {
          /* Server still deduplicates this session. */
        }
      }
      const b = await api.feed(feedRequest.current);
      if (currentUser.current !== owner) return;
      feedRequest.current = null;
      try {
        localStorage.removeItem(requestKey);
      } catch {
        /* A replay remains harmless. */
      }
      setDashboard((d) => ({ ...d, buddy: b }));
      setTimeout(() => setFeeding(false), 1600);
      notify("A good deed. A happy dog.");
    } catch (e) {
      if (currentUser.current === owner) notify((e as Error).message);
      setFeeding(false);
    }
  }
  const selectionSummary = wishlistSummary(filters, catalog);
  const count = selectionSummary.count;
  const anyCharms = filters.category_ids.includes(anyCharmCategory);
  const shownProducts = catalog.filter(
    (p) =>
      p.category !== "bag" &&
      (brand === "All" || p.brand === brand) &&
      p.name.toLowerCase().includes(productQuery.toLowerCase()),
  );
  const sorted = [...result.stores].sort((a, b) =>
    sort === "distance"
      ? a.distance_miles - b.distance_miles
      : Math.max(
          ...b.matches.map((m) => Date.parse(m.observed_at ?? "") || 0),
        ) -
        Math.max(...a.matches.map((m) => Date.parse(m.observed_at ?? "") || 0)),
  );
  const unread = dashboard.notifications.filter((n) => !n.read_at).length;
  return (
    <div className="app-shell">
      <header className="topbar">
        <a
          className="wordmark"
          href={demo ? "?demo=1" : "/"}
          aria-label="Little Finds home"
        >
          <span className="logo-flower">
            <Sparkles size={24} />
          </span>
          little finds<span className="wordmark-dot">.</span>
        </a>
        <nav aria-label="Main navigation">
          <button
            className={view === "map" ? "active" : ""}
            onClick={() => setView("map")}
          >
            <Compass size={17} />
            Explore
          </button>
          <button
            className={view === "saved" ? "active" : ""}
            onClick={() => {
              setView("saved");
              if (!user) needAccount("login");
            }}
          >
            <Heart size={17} />
            My wishlist
          </button>
          <button
            className={view === "buddy" ? "active" : ""}
            onClick={() => setView("buddy")}
          >
            <Cookie size={17} />
            My buddy
          </button>
        </nav>
        <div className="header-right">
          {demo && (
            <span className="demo-pill">
              DEMO <span>· sample data</span>
            </span>
          )}
          <button
            className="icon-button notification-button"
            aria-label={`Inbox, ${unread} unread`}
            onClick={() => needAccount("inbox")}
          >
            <Bell size={20} />
            {unread > 0 && <i />}
          </button>
          <button
            className="avatar"
            aria-label={user ? "Account settings" : "Sign in"}
            onClick={() => setModal(user ? "account" : "login")}
          >
            {user ? "J" : <UserRound size={18} />}
          </button>
        </div>
      </header>
      {staging && (
        <div
          className="staging-banner"
          role="note"
          aria-label="Staging environment"
        >
          <strong>Staging · test environment</strong>
          <span>Test sightings are not real product availability.</span>
        </div>
      )}
      <main>
        <section className="page-heading">
          <div>
            <div className="eyebrow">
              <Leaf size={14} /> LITTLE THINGS. LOVELY PLACES.
            </div>
            <h1>
              {view === "map"
                ? "Your next little happy thing."
                : view === "saved"
                  ? "Good things worth looking for."
                  : view === "buddy"
                    ? "A little friend. A big appetite."
                    : "Behind the little finds."}
            </h1>
            <p>
              {view === "map"
                ? "Find your favorites nearby. Share a sighting. Make someone’s day."
                : view === "saved"
                  ? "Your saved hunts, all in one cozy corner."
                  : view === "buddy"
                    ? "Every helpful visit makes the community — and your beagle — a little fuller."
                    : "Review contributions, care for the catalog, and keep our community useful."}
            </p>
          </div>
          <button className="button" onClick={() => needAccount("sighting")}>
            <Plus size={18} />
            Share a sighting
          </button>
        </section>
        {view === "map" ? (
          <>
            <section
              className="search-toolbar"
              aria-label="Location and search settings"
            >
              <LocationSearch
                area={area}
                isDemo={demo}
                onLocate={locate}
                onSelect={(option) => {
                  clearSelectedStore();
                  setArea(option.label);
                  changeFilters((f) => ({
                    ...f,
                    center: {
                      latitude: option.latitude,
                      longitude: option.longitude,
                    },
                    radius_miles: 25,
                    scope: "radius",
                    zoom: 11,
                    offset: 0,
                    bounds: undefined,
                  }));
                }}
              />
              <label className="radius-select">
                <span>Area</span>
                <select
                  aria-label="Search area"
                  value={
                    filters.bounds
                      ? "map"
                      : filters.scope === "us"
                        ? "us"
                        : String(filters.radius_miles)
                  }
                  onChange={(e) => {
                    clearSelectedStore();
                    const national = e.target.value === "us";
                    if (national) setArea("United States");
                    else if (filters.scope === "us") setArea("Your map center");
                    changeFilters({
                      ...filters,
                      ...(national
                        ? {
                            center: { latitude: 39.8283, longitude: -98.5795 },
                            zoom: 3,
                          }
                        : {}),
                      scope: national ? "us" : "radius",
                      radius_miles: national ? 3000 : Number(e.target.value),
                      offset: 0,
                      bounds: undefined,
                    });
                  }}
                >
                  {filters.bounds && (
                    <option value="map" disabled>
                      Map area
                    </option>
                  )}
                  {[5, 10, 25, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n} miles
                    </option>
                  ))}
                  {filters.scope !== "us" &&
                    ![5, 10, 25, 50, 100].includes(filters.radius_miles) && (
                      <option value={filters.radius_miles}>
                        {filters.radius_miles.toLocaleString("en-US")} miles
                      </option>
                    )}
                  <option value="us">United States</option>
                </select>
                <ChevronDown size={14} />
              </label>
              <div className="toolbar-divider" />
              <span className="recent-only">
                <span className="status-dot" />
                {filters.layers.older ||
                filters.layers.mentions ||
                filters.layers.stockists
                  ? "Additional layers on"
                  : "Fresh sightings · last 24 hours"}
              </span>
              <button
                className="text-button save-hunt"
                onClick={() => needAccount("save")}
              >
                <Bookmark size={16} />
                {activeHunt ? "Save hunt changes" : "Save this hunt"}
              </button>
              <button
                className="mobile-filters text-button"
                onClick={() => setShowFilters(!showFilters)}
              >
                <SlidersHorizontal size={18} />
                Filters ({count})
              </button>
            </section>
            <div className="dashboard-layout">
              <aside
                ref={filterPanel}
                role={showFilters ? "dialog" : undefined}
                aria-modal={showFilters ? true : undefined}
                className={`filter-sidebar ${showFilters ? "show" : ""}`}
                aria-label="Product filters"
              >
                <div className="sidebar-title">
                  <h2>Your little wishlist</h2>
                  <span
                    className="count-badge"
                    aria-label={`${selectionSummary.title} selected`}
                  >
                    {count}
                  </span>
                  <button
                    className="mobile-filters icon-button"
                    onClick={() => setShowFilters(false)}
                    aria-label="Close filters"
                  >
                    <X size={18} />
                  </button>
                </div>
                <p className="small muted">
                  {isAllProducts(filters)
                    ? "Showing every catalog product."
                    : "A place appears when it has any of your selections."}
                </p>
                <div
                  className="brand-tabs"
                  role="group"
                  aria-label="Filter products by brand"
                >
                  {["All", "NeeDoh", "Jellycat"].map((b) => (
                    <button
                      className={brand === b ? "active" : ""}
                      key={b}
                      aria-pressed={brand === b}
                      onClick={() => setBrand(b)}
                    >
                      {b}
                    </button>
                  ))}
                </div>
                <div className="mini-search">
                  <Search size={15} />
                  <input
                    aria-label="Search products"
                    placeholder="Find a favorite…"
                    value={productQuery}
                    onChange={(e) => setProductQuery(e.target.value)}
                  />
                </div>
                <div className="wishlist-options">
                  {["NeeDoh", "Jellycat"]
                    .filter((b) => brand === "All" || brand === b)
                    .map((b) => (
                      <section key={b}>
                        <h3>
                          {b}
                          <span>
                            {
                              shownProducts.filter(
                                (p) =>
                                  p.brand === b && p.category !== "bag_charm",
                              ).length
                            }
                          </span>
                        </h3>
                        {shownProducts
                          .filter(
                            (p) => p.brand === b && p.category !== "bag_charm",
                          )
                          .map((p) => (
                            <div className="product-option" key={p.id}>
                              <label>
                                <input
                                  type="checkbox"
                                  checked={productIsSelected(filters, p)}
                                  onChange={() => chooseProduct(p.id)}
                                />
                                <span className="custom-check">
                                  <Check size={12} />
                                </span>
                                <ProductArt product={p} size={34} />
                                <span>
                                  {p.name}
                                  {filters.variants?.[p.id] && (
                                    <small>{filters.variants[p.id]}</small>
                                  )}
                                </span>
                              </label>
                              <button
                                className="variant-button"
                                aria-label={`Set size or color for ${p.name}`}
                                title="Size or color"
                                onClick={() => {
                                  setVariantProduct(p.id);
                                  setVariantText(
                                    filters.variants?.[p.id] ?? "",
                                  );
                                }}
                              >
                                <Settings2 size={12} />
                              </button>
                            </div>
                          ))}
                      </section>
                    ))}
                  {(brand === "All" || brand === "Jellycat") && (
                    <fieldset className="charm-filter">
                      <legend>Jellycat bag charms</legend>
                      <label className="category-option">
                        <input
                          type="radio"
                          name="charm-mode"
                          checked={anyCharms}
                          onChange={() =>
                            changeFilters((f) =>
                              chooseCharmMode(f, "any", catalog),
                            )
                          }
                        />
                        <span className="charm-icon">✧</span>
                        <span>
                          Any Jellycat bag charm
                          <small>
                            All bag charms, including future releases
                          </small>
                        </span>
                      </label>
                      <label className="category-option">
                        <input
                          type="radio"
                          name="charm-mode"
                          checked={!anyCharms && !isAllProducts(filters)}
                          onChange={() =>
                            changeFilters((f) =>
                              chooseCharmMode(f, "specific", catalog),
                            )
                          }
                        />
                        <span>
                          Choose specific charms
                          <small>Select the individual charms below</small>
                        </span>
                      </label>
                      {!anyCharms &&
                        shownProducts
                          .filter((p) => p.category === "bag_charm")
                          .map((p) => (
                            <div className="product-option" key={p.id}>
                              <label>
                                <input
                                  type="checkbox"
                                  checked={productIsSelected(filters, p)}
                                  onChange={() => chooseProduct(p.id)}
                                />
                                <span className="custom-check">
                                  <Check size={12} />
                                </span>
                                <ProductArt product={p} size={34} />
                                <span>
                                  {p.name}
                                  {filters.variants?.[p.id] && (
                                    <small>{filters.variants[p.id]}</small>
                                  )}
                                </span>
                              </label>
                              <button
                                className="variant-button"
                                aria-label={`Set size or color for ${p.name}`}
                                title="Size or color"
                                onClick={() => {
                                  setVariantProduct(p.id);
                                  setVariantText(
                                    filters.variants?.[p.id] ?? "",
                                  );
                                }}
                              >
                                <Settings2 size={14} />
                              </button>
                            </div>
                          ))}
                    </fieldset>
                  )}
                </div>
                <div className="selection-actions">
                  <button
                    onClick={() =>
                      changeFilters({
                        ...filters,
                        product_ids: defaultProductIds,
                        category_ids: defaultCategoryIds,
                        variants: {},
                        offset: 0,
                      })
                    }
                  >
                    Select favorites
                  </button>
                  <button
                    onClick={() =>
                      changeFilters({
                        ...filters,
                        product_ids: [],
                        category_ids: [],
                        variants: {},
                        offset: 0,
                      })
                    }
                  >
                    Show all
                  </button>
                </div>
                <details className="layer-picker">
                  <summary>
                    <LayersIcon />
                    More to explore
                    <ChevronDown size={14} />
                  </summary>
                  {(
                    [
                      ["older", "Older sightings", "Includes mixed reports"],
                      [
                        "mentions",
                        "Public mentions",
                        "Useful leads, not stock checks",
                      ],
                      ["stockists", "Brand stockists", "Places to check"],
                    ] as const
                  ).map(([k, label, sub]) => (
                    <label className="check-row" key={k}>
                      <input
                        type="checkbox"
                        checked={filters.layers[k]}
                        onChange={() => toggleLayer(k)}
                      />
                      <span>
                        {label}
                        <small>{sub}</small>
                      </span>
                    </label>
                  ))}
                </details>
                <div className="sidebar-buddy">
                  <div>
                    <span className="eyebrow">MEET YOUR LITTLE SIDEKICK</span>
                    <h3>
                      Good finds.
                      <br />
                      Good dog.
                    </h3>
                    <button
                      className="text-button"
                      onClick={() => setView("buddy")}
                    >
                      Say hello <ArrowRight size={14} />
                    </button>
                  </div>
                  <Buddy fed={dashboard.buddy.total_fed} compact />
                </div>
              </aside>
              <div className="map-column">
                <div className="map-topline">
                  <span>
                    <MapPin size={15} />
                    {area}
                  </span>
                  <span>
                    {result.recent_stores} places with fresh sightings
                  </span>
                </div>
                <MapCanvas
                  result={{ ...result, stores: sorted }}
                  selected={selected?.id ?? null}
                  onSelect={(s) => {
                    directStore.current = null;
                    chooseStore(s);
                  }}
                  filters={filters}
                  onMove={(change) => {
                    clearSelectedStore();
                    if (change.bounds) setArea("Your map area");
                    changeFilters(
                      (f) => ({
                        ...f,
                        ...change,
                        scope:
                          change.scope ?? (change.bounds ? "us" : "radius"),
                        offset: 0,
                      }),
                      false,
                    );
                  }}
                  onLocate={locate}
                  onCluster={exploreCluster}
                  isDemo={demo}
                />
                <div className="map-legend">
                  <span>
                    <i className="legend-dot green" />
                    Recent sighting
                  </span>
                  <span>
                    <i className="legend-dot coral" />
                    Selected shop
                  </span>
                  <span className="map-legend-note">
                    <ShieldCheck size={14} />
                    Community spotted. Always worth a quick call.
                  </span>
                </div>
                <div className="community-note">
                  <span className="note-sparkle">✦</span>
                  <div>
                    <strong>A little local knowledge goes a long way.</strong>
                    <p>
                      Seen something lovely? Your sighting could make the next
                      person’s day.
                    </p>
                  </div>
                  <button
                    onClick={() => needAccount("sighting")}
                    aria-label="Share your sighting"
                  >
                    <ArrowRight size={20} />
                  </button>
                </div>
              </div>
              <aside className="results-sidebar" aria-label="Matching shops">
                <div className="results-heading">
                  <h2 ref={resultsHeading} tabIndex={-1}>
                    {selected
                      ? "A lovely little lead"
                      : "Places to find a little joy"}
                  </h2>
                  {selected ? (
                    <button
                      className="text-button"
                      onClick={() => {
                        directStore.current = null;
                        restoreStoreFocus.current = true;
                        setSelected(null);
                      }}
                    >
                      <ArrowLeft size={14} />
                      All places
                    </button>
                  ) : (
                    <>
                      <div>
                        <span role="status" aria-live="polite">
                          {loading
                            ? "Looking around…"
                            : `${result.total_matches} ${result.total_matches === 1 ? "place matches" : "places match"} your wishlist`}
                        </span>
                        <button
                          className="sort-button"
                          aria-label={
                            sort === "distance"
                              ? "Sort by freshest"
                              : "Sort by distance"
                          }
                          title="Change sort"
                          onClick={() =>
                            setSort(
                              sort === "distance" ? "freshest" : "distance",
                            )
                          }
                        >
                          <ArrowDownUp size={14} />
                          {sort === "distance" ? "Nearest" : "Freshest"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
                {error && (
                  <div role="alert" className="error-card">
                    <strong>We couldn’t refresh the map.</strong>
                    <p>{error}</p>
                    <button className="text-button" onClick={refresh}>
                      Try again
                    </button>
                  </div>
                )}
                {selected ? (
                  <StoreDetail
                    store={selected}
                    headingRef={detailHeading}
                    viewerScope={userId ?? "anonymous"}
                    history={history}
                    historyLoading={historyLoading}
                    historyError={historyError}
                    onRetryHistory={() => setHistoryRefresh((v) => v + 1)}
                    catalog={catalog}
                    onSighting={() => needAccount("sighting")}
                    onReport={(id) => {
                      setReportId(id);
                      needAccount("report");
                    }}
                    onBlock={async (id) => {
                      if (!user) {
                        needAccount("login");
                        return;
                      }
                      try {
                        if (!demo)
                          await rpc("block_contributor", {
                            p_contributor_id: id,
                          });
                        notify(
                          "Contributor blocked. Their sightings will be hidden.",
                        );
                        setHistory((h) =>
                          h.filter((e) => e.contributor_id !== id),
                        );
                        if (demo)
                          changeFilters((f) => ({
                            ...f,
                            blocked_contributors: [
                              ...(f.blocked_contributors ?? []),
                              id,
                            ],
                          }));
                        else void refresh();
                        void refreshDashboard();
                      } catch (e) {
                        notify((e as Error).message);
                      }
                    }}
                  />
                ) : loading ? (
                  <div className="loading-state">
                    <span className="spinner" />
                    Looking for lovely things…
                  </div>
                ) : (
                  <ResultsBrowser
                    clusters={result.clusters}
                    storeCount={sorted.length}
                    onCluster={exploreCluster}
                    storeList={
                      <div className="results-list">
                        {sorted.map((store, i) => {
                          const summary = representativeMatch(store.matches);
                          return (
                            <button
                              className="store-card"
                              key={store.id}
                              data-store-id={store.id}
                              onClick={() => {
                                directStore.current = null;
                                chooseStore(store);
                              }}
                            >
                              <div className="store-card-top">
                                <span className="store-number">{i + 1}</span>
                                <span className="store-distance">
                                  {store.distance_miles < 1
                                    ? store.distance_miles.toFixed(1)
                                    : Math.round(store.distance_miles)}{" "}
                                  mi
                                  <ChevronRight size={14} />
                                </span>
                              </div>
                              <h3>{store.name}</h3>
                              <p>{store.address}</p>
                              <div
                                className={`status-line ${store.recent_count ? "fresh" : ""}`}
                              >
                                <span className="status-dot" />
                                {store.recent_count
                                  ? "Seen recently"
                                  : titleFor(summary?.state ?? "stockist")}
                                <span>· {age(summary?.observed_at)}</span>
                              </div>
                              <div className="store-products">
                                {store.matches
                                  .filter((m) => m.product_id)
                                  .slice(0, 3)
                                  .map((m, j) => {
                                    const p = catalog.find(
                                      (p) => p.id === m.product_id,
                                    );
                                    return p ? (
                                      <span key={`${p.id}-${j}`} title={p.name}>
                                        <ProductArt product={p} size={27} />
                                        {p.name}
                                      </span>
                                    ) : null;
                                  })}
                                {store.matches.filter((m) => m.product_id)
                                  .length > 3 && (
                                  <span className="more-products">
                                    +
                                    {store.matches.filter((m) => m.product_id)
                                      .length - 3}
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                        {result.has_more && (
                          <button
                            className="button secondary"
                            disabled={loadingMore}
                            onClick={async () => {
                              const sequence = searchSequence.current;
                              setLoadingMore(true);
                              try {
                                const next = await api.search({
                                  ...filters,
                                  offset: result.stores.length,
                                });
                                if (sequence === searchSequence.current)
                                  setResult((r) => ({
                                    ...next,
                                    stores: [
                                      ...r.stores,
                                      ...next.stores.filter(
                                        (s) =>
                                          !r.stores.some((v) => v.id === s.id),
                                      ),
                                    ],
                                  }));
                              } catch (e) {
                                if (sequence === searchSequence.current)
                                  notify((e as Error).message);
                              } finally {
                                setLoadingMore(false);
                              }
                            }}
                          >
                            {loadingMore
                              ? "Loading places…"
                              : "Load more places"}
                          </button>
                        )}
                      </div>
                    }
                    emptyState={
                      <div className="empty-state">
                        <span className="empty-flower">✿</span>
                        <h3>
                          {configured || demo
                            ? "No fresh finds here just yet."
                            : "Let’s find something lovely."}
                        </h3>
                        <p>
                          {configured || demo
                            ? "A quiet map is an honest map. Try a wider area or turn on older sightings and stockists."
                            : "Our shared map is getting ready. Explore the sample hunt to try the wishlist, sightings, and beagle."}
                        </p>
                        {configured || demo ? (
                          <button
                            className="button secondary"
                            onClick={() =>
                              changeFilters((f) => ({
                                ...f,
                                layers: {
                                  older: true,
                                  mentions: true,
                                  stockists: true,
                                },
                              }))
                            }
                          >
                            Explore more leads
                          </button>
                        ) : (
                          <a className="button" href="?demo=1">
                            Explore demo <ArrowRight size={15} />
                          </a>
                        )}
                        {(configured || demo) && (
                          <button
                            className="button secondary"
                            onClick={() => setModal("directory")}
                          >
                            <MapPin size={16} /> Browse known shops
                          </button>
                        )}
                        <button
                          className="text-button"
                          onClick={() => needAccount("suggest")}
                        >
                          Know a shop? Add a little lead <Plus size={14} />
                        </button>
                      </div>
                    }
                  />
                )}
              </aside>
            </div>
          </>
        ) : view === "saved" ? (
          <section className="saved-page">
            {dashboard.saved_searches.length ? (
              dashboard.saved_searches.map((s) => (
                <article className="saved-card" key={s.id}>
                  <Bookmark size={23} />
                  <h2>{s.name}</h2>
                  <p>{searchAreaSummary(s.filters)}</p>
                  <p
                    className="saved-products"
                    title={wishlistSummary(s.filters, catalog).detail}
                  >
                    {wishlistSummary(s.filters, catalog).detail}
                  </p>
                  <span className="status-line fresh">
                    <Bell size={14} />
                    {s.alerts_enabled ? "Alerts are on" : "Alerts are off"}
                  </span>
                  <button
                    className="button"
                    onClick={() => {
                      clearSelectedStore();
                      changeFilters(s.filters);
                      setActiveHunt(s);
                      setArea(s.name);
                      setView("map");
                    }}
                  >
                    Explore this hunt <ArrowRight size={16} />
                  </button>
                  <button
                    className="text-button"
                    onClick={() => {
                      clearSelectedStore();
                      changeFilters(s.filters);
                      setActiveHunt(s);
                      setEditingHunt(s);
                      setModal("save");
                    }}
                  >
                    Edit hunt
                  </button>
                  <button
                    className="text-button"
                    disabled={savingHunt === s.id}
                    onClick={async () => {
                      const owner = currentUser.current;
                      setSavingHunt(s.id ?? null);
                      try {
                        const saved = await api.save({
                          ...s,
                          alerts_enabled: !s.alerts_enabled,
                        });
                        if (activeHunt?.id === s.id) setActiveHunt(saved);
                        if (currentUser.current === owner)
                          await refreshDashboard();
                      } catch (e) {
                        if (currentUser.current === owner)
                          notify((e as Error).message);
                      } finally {
                        setSavingHunt(null);
                      }
                    }}
                  >
                    {s.alerts_enabled ? "Turn alerts off" : "Turn alerts on"}
                  </button>
                  <button
                    className="text-button"
                    disabled={savingHunt === s.id}
                    onClick={async () => {
                      const owner = currentUser.current;
                      setSavingHunt(s.id ?? null);
                      try {
                        if (demo) {
                          const d = readDemo();
                          d.dashboard.saved_searches =
                            d.dashboard.saved_searches.filter(
                              (v) => v.id !== s.id,
                            );
                          writeDemo(d);
                        } else await rpc("delete_saved_search", { p_id: s.id });
                        if (activeHunt?.id === s.id) setActiveHunt(undefined);
                        if (currentUser.current === owner)
                          await refreshDashboard();
                      } catch (e) {
                        if (currentUser.current === owner)
                          notify((e as Error).message);
                      } finally {
                        setSavingHunt(null);
                      }
                    }}
                  >
                    Remove saved hunt
                  </button>
                </article>
              ))
            ) : (
              <div className="empty-state">
                <Heart size={40} />
                <h2>Your next adventure belongs here.</h2>
                <p>Choose your favorite things and save a hunt from the map.</p>
                <button className="button" onClick={() => setView("map")}>
                  Explore the map <ArrowRight size={17} />
                </button>
              </div>
            )}
          </section>
        ) : view === "buddy" ? (
          <section className="buddy-page">
            <div className="buddy-stage">
              <span className="eyebrow">YOUR VERY GOOD LITTLE SIDEKICK</span>
              <h2>Meet Biscuit.</h2>
              <p>Excellent company. Enthusiastic snacker.</p>
              <Buddy fed={dashboard.buddy.total_fed} feeding={feeding} />
              <div className="buddy-title">
                <Sparkles size={18} />
                {buddyStages[buddyStage(dashboard.buddy.total_fed)]}
              </div>
              <div className="growth-dots">
                {[0, 1, 5, 15, 30].map((n, i) => (
                  <span
                    key={n}
                    className={dashboard.buddy.total_fed >= n ? "reached" : ""}
                    title={`${n} feeds`}
                  >
                    {i + 1}
                  </span>
                ))}
              </div>
            </div>
            <div className="buddy-explainer">
              <div className="biscuit-jar">
                <Cookie size={33} />
                <strong>{dashboard.buddy.available_treats}</strong>
                <span>biscuits in your jar</span>
              </div>
              <button
                className="button feed-button"
                disabled={feeding}
                onClick={feed}
              >
                <Cookie size={18} />
                {feeding ? "Nom nom nom…" : "Feed Biscuit"}
              </button>
              <p className="small muted">
                {dashboard.buddy.pending_treats
                  ? `${dashboard.buddy.pending_treats} biscuit waiting for review.`
                  : "A helpful visit earns a happy little treat."}
              </p>
              <hr />
              <h3>Little acts. Lovely rewards.</h3>
              <ol className="how-it-works">
                <li>
                  <span>1</span>
                  <div>
                    <strong>Share what you found</strong>
                    <p>
                      One shop visit. As many useful observations as you like.
                    </p>
                  </div>
                </li>
                <li>
                  <span>2</span>
                  <div>
                    <strong>Give us a moment to check</strong>
                    <p>
                      Accepted visits earn a biscuit. “Couldn’t find it” helps
                      too.
                    </p>
                  </div>
                </li>
                <li>
                  <span>3</span>
                  <div>
                    <strong>Watch Biscuit get cozier</strong>
                    <p>
                      No streaks. No hungry days. Just a very round, very good
                      friend.
                    </p>
                  </div>
                </li>
              </ol>
              <button
                className="text-button"
                onClick={() => needAccount("sighting")}
              >
                Share your next sighting <ArrowRight size={17} />
              </button>
            </div>
          </section>
        ) : (
          <AdminPanel
            viewerScope={userId ?? "anonymous"}
            data={adminData}
            catalog={catalog}
            onUpdate={async () => {
              await refreshAdmin();
              setCatalog(await api.catalog());
              refreshDashboard();
              refresh();
            }}
          />
        )}
      </main>
      <footer>
        <span>
          <Leaf size={14} />
          Little things bring us together.
        </span>
        <div>
          <button onClick={() => setModal("about")}>About</button>
          <button onClick={() => setModal("privacy")}>Privacy</button>
          <button onClick={() => setModal("guidelines")}>Community care</button>
          {(dashboard.is_admin || demo) && (
            <button onClick={() => setView("admin")}>
              {demo ? "Review demo" : "Admin"}
            </button>
          )}
        </div>
        <span className="made-in">
          <span aria-hidden="true">✧</span> Designed in the USA
        </span>
      </footer>
      {toast && (
        <div className="toast" role="status">
          <Check size={18} />
          {toast}
          <button
            onClick={() => setToast("")}
            aria-label="Dismiss notification"
          >
            <X size={16} />
          </button>
        </div>
      )}
      {modal && (
        <Modal
          title={
            {
              login: "Welcome to your happy place.",
              sighting: "Share a little find.",
              save: "Save a little hunt.",
              inbox: "A little good news.",
              account: "Your cozy corner.",
              suggest: "Put a lovely shop on the map.",
              directory: "Known shops to explore.",
              report: "Help keep our community useful.",
              about: "Made for the little joys.",
              privacy: "Your privacy matters.",
              guidelines: "A little community care.",
              buddy: "Your little sidekick.",
            }[modal] ?? "Little Finds"
          }
          onClose={() => setModal(null)}
          wide={modal === "sighting"}
        >
          {modal === "login" ? (
            <LoginForm
              onBrowse={() => {
                afterLogin.current = null;
                setModal(null);
              }}
              onDone={() => {
                setModal(
                  afterLogin.current === "login" ? null : afterLogin.current,
                );
                afterLogin.current = null;
                refreshDashboard();
              }}
            />
          ) : modal === "sighting" ? (
            <SightingForm
              stores={demo ? demoStores : result.stores}
              catalog={catalog}
              key={userId ?? "guest"}
              draftOwner={userId ?? "guest"}
              initialStore={selected?.id}
              onDone={() => {
                refreshDashboard();
                refresh();
              }}
            />
          ) : modal === "save" ? (
            <SaveSearchForm
              key={editingHunt?.id ?? "new-hunt"}
              filters={filters}
              catalog={catalog}
              existing={editingHunt}
              onDone={(saved) => {
                setActiveHunt(saved);
                setEditingHunt(undefined);
                setModal(null);
                refreshDashboard();
                notify(
                  "Your hunt is saved. Good things are worth looking for.",
                );
              }}
            />
          ) : modal === "directory" ? (
            <DirectoryBrowser onSelect={openListedStore} />
          ) : modal === "suggest" ? (
            <SuggestStoreForm onDone={() => {}} />
          ) : modal === "inbox" ? (
            <div className="inbox-list">
              {dashboard.notifications.length ? (
                dashboard.notifications.map((n) => (
                  <article key={n.id} className={n.read_at ? "" : "unread"}>
                    <Bell size={18} />
                    <div>
                      <h3>{n.title}</h3>
                      <p>{n.body}</p>
                      <small>{age(n.created_at)}</small>
                      {n.store_id && (
                        <button
                          className="text-button"
                          onClick={() => void openListedStore(n.store_id!)}
                        >
                          View shop <ArrowRight size={14} />
                        </button>
                      )}
                    </div>
                    <button
                      className="icon-button"
                      aria-label="Mark notification read"
                      disabled={Boolean(n.read_at)}
                      onClick={async () => {
                        const owner = currentUser.current;
                        try {
                          if (!demo)
                            await rpc("mark_notification_read", { p_id: n.id });
                          else {
                            const d = readDemo();
                            d.dashboard.notifications =
                              d.dashboard.notifications.map((v) =>
                                v.id === n.id
                                  ? { ...v, read_at: new Date().toISOString() }
                                  : v,
                              );
                            writeDemo(d);
                          }
                          if (currentUser.current === owner)
                            setDashboard((d) => ({
                              ...d,
                              notifications: d.notifications.map((v) =>
                                v.id === n.id
                                  ? { ...v, read_at: new Date().toISOString() }
                                  : v,
                              ),
                            }));
                        } catch (e) {
                          if (currentUser.current === owner)
                            notify((e as Error).message);
                        }
                      }}
                    >
                      <Check size={16} />
                    </button>
                  </article>
                ))
              ) : (
                <div className="empty-state">
                  <Bell size={32} />
                  <h3>All quiet in your cozy corner.</h3>
                  <p>
                    New matching sightings and review updates will land here.
                  </p>
                </div>
              )}
            </div>
          ) : modal === "account" ? (
            <div className="stack">
              <p>
                Signed in as <strong>{user}</strong>
              </p>
              {demo && (
                <p className="demo-note">
                  This is a local demo account. It does not create or publish
                  real data.
                </p>
              )}
              <button className="button secondary" onClick={signOut}>
                Sign out
              </button>
              <button
                className="text-button danger"
                onClick={() => setModal("delete")}
              >
                Delete my account and contributions
              </button>
            </div>
          ) : modal === "delete" ? (
            <div className="stack">
              <p>
                This permanently removes your account, contributed sightings and
                photos, saved hunts, and buddy progress.
              </p>
              <button
                className="button danger-button"
                onClick={async () => {
                  try {
                    if (demo) {
                      localStorage.removeItem("little-finds:demo:v2");
                      location.href = "?demo=1";
                      return;
                    }
                    const { error } =
                      await supabase!.functions.invoke("delete-account");
                    if (error) throw error;
                    await supabase!.auth.signOut();
                    localStorage.removeItem(
                      `little-finds:draft:${encodeURIComponent(userId ?? "guest")}`,
                    );
                    const owner = userId;
                    if (owner) {
                      await saveDraftPhoto(
                        `little-finds:draft:${encodeURIComponent(owner)}`,
                        null,
                      ).catch(() => {});
                      localStorage.removeItem(filtersKey(owner));
                      localStorage.removeItem(
                        `little-finds:feed-request:${owner}`,
                      );
                    }
                    setModal(null);
                    notify("Your account and contributions have been deleted.");
                  } catch (e) {
                    notify((e as Error).message);
                  }
                }}
              >
                Permanently delete account
              </button>
            </div>
          ) : modal === "report" ? (
            <form
              className="stack"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  if (!demo)
                    await rpc("report_content", {
                      p_evidence_id: reportId,
                      p_reason: reportReason,
                    });
                  setModal(null);
                  notify("Thank you. Your report is in the review basket.");
                } catch (e) {
                  notify((e as Error).message);
                }
              }}
            >
              <label>
                What should we look at?
                <textarea
                  required
                  maxLength={500}
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  placeholder="Incorrect product, spam, inappropriate content…"
                />
              </label>
              <button className="button">
                <Flag size={17} />
                Send report
              </button>
            </form>
          ) : modal === "about" ? (
            <div className="prose">
              <Buddy compact />
              <h3>A little closer to your next happy thing.</h3>
              <p>
                Little Finds brings collectors together around local shops,
                useful sightings, and the joy of finding a favorite.
              </p>
              <p>
                Our original beagle Biscuit celebrates helpful contributions,
                one well-earned treat at a time.
              </p>
              <strong>Designed in the USA</strong>
              <p className="small muted">
                Independent community project. NeeDoh and Jellycat names
                identify the products people are searching for; they do not
                imply affiliation.
              </p>
            </div>
          ) : modal === "privacy" ? (
            <div className="prose">
              <p>
                Browsing does not require an account. We use your email for
                sign-in and store your saved hunts, contributions, and optional
                notification preferences when you create one.
              </p>
              <p>
                Location access is optional and requested only when you tap
                “Near me.” We do not record travel history. A submitted report
                identifies the shop and visit time. Photo location metadata is
                stripped before storage.
              </p>
              <p>
                Report photos stay private during review. Approved contribution
                details may be visible to other collectors. Account settings
                include permanent deletion of your account and contributions.
              </p>
              <p>
                Community contributions are not automatically opted into model
                training. Source permissions and separate contributor consent
                are required.
              </p>
              <p className="small muted">
                This beta privacy notice describes the implemented data flow.
                Operator contact and final policy details must be configured
                before public release.
              </p>
            </div>
          ) : modal === "guidelines" ? (
            <div className="prose">
              <h3>Be kind. Be specific. Be helpful.</h3>
              <p>
                Report the actual shop, product, and visit time. “Couldn’t find
                it” is useful — it does not mean a shop never carries it. Share
                shelf photos without faces or personal information.
              </p>
              <p>
                Every report represents an observation, not a guarantee. Stock
                can change. A quick call is always welcome before a longer trip.
              </p>
              <p>
                Use report or block on any contribution that feels wrong. New
                contributions are reviewed, and duplicate or misleading reports
                cannot earn extra treats.
              </p>
            </div>
          ) : null}
        </Modal>
      )}
      {variantProduct && (
        <Modal
          title="A particular size or color?"
          onClose={() => setVariantProduct(null)}
        >
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              changeFilters((f) =>
                setWishlistVariant(f, variantProduct, variantText, catalog),
              );
              setVariantProduct(null);
            }}
          >
            <p className="muted">
              Leave this blank to find any variant. A specific variant only
              matches reports that identify it exactly.
            </p>
            <label>
              Reported size or color
              <input
                value={variantText}
                onChange={(e) => setVariantText(e.target.value)}
                placeholder="Any size or color"
                maxLength={80}
              />
            </label>
            <button className="button">
              Save preference <Check size={17} />
            </button>
          </form>
        </Modal>
      )}
    </div>
  );
}
function LayersIcon() {
  return <SlidersHorizontal size={15} />;
}
function StoreDetail({
  store,
  headingRef,
  viewerScope,
  history,
  historyLoading,
  historyError,
  onRetryHistory,
  catalog,
  onSighting,
  onReport,
  onBlock,
}: {
  store: StoreMatch;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  viewerScope: string;
  history: Evidence[];
  historyLoading: boolean;
  historyError: string;
  onRetryHistory: () => void;
  catalog: Product[];
  onSighting: () => void;
  onReport: (id: string) => void;
  onBlock: (id: string) => void;
}) {
  const { photos, preview, clear } = useEvidencePhotos(
    `${viewerScope}:${store.id}`,
  );
  const photo = Object.values(photos)[0];
  const [photoError, setPhotoError] = useState("");
  useEffect(() => {
    setPhotoError("");
  }, [store.id, viewerScope]);
  useEffect(() => {
    for (const id of Object.keys(photos)) {
      if (!history.some((e) => e.id === id && e.photo_path)) clear(id);
    }
  }, [history]);
  return (
    <div className="store-detail">
      <div className="detail-illustration">
        <span>✦</span>
        <div className="shop-awning">little local shop</div>
        <div className="shop-front">
          <span>HELLO</span>
          <Heart size={26} />
        </div>
        <Leaf size={34} />
      </div>
      <h2 ref={headingRef} tabIndex={-1} className="detail-focus-heading">
        {store.name}
      </h2>
      <p>
        <MapPin size={15} />
        {store.address}
        <br />
        {store.city}, {store.state} {store.zip}
      </p>
      <div className="detail-actions">
        <a
          className="button"
          href={`https://maps.apple.com/?daddr=${encodeURIComponent(`${store.latitude},${store.longitude}`)}`}
          target="_blank"
          rel="noreferrer"
        >
          <Navigation size={16} />
          Directions
        </a>
        <button className="button secondary" onClick={onSighting}>
          <Plus size={16} />I visited
        </button>
      </div>
      <div className="detail-heading">
        <h3>
          {store.matches.length ? "On your wishlist" : "Stock not checked"}
        </h3>
        <ShieldCheck size={16} />
      </div>
      {!store.matches.length && (
        <p className="small muted">
          This directory listing does not establish a current match for your
          wishlist. Check the history for previous reports and call the shop
          before making a trip.
        </p>
      )}
      {store.matches.map((m, i) => {
        const p = catalog.find((p) => p.id === m.product_id);
        return (
          <div className="detail-match" key={i}>
            {p ? <ProductArt product={p} size={44} /> : <MapPin size={27} />}
            <div>
              <strong>{p?.name ?? "Brand stockist"}</strong>
              <span
                className={`status-line ${m.state === "recent" ? "fresh" : ""}`}
              >
                {titleFor(m.state)} ·{" "}
                {m.observed_at ? (
                  <time
                    dateTime={m.observed_at}
                    title={new Date(m.observed_at).toLocaleString()}
                  >
                    {age(m.observed_at)}
                  </time>
                ) : (
                  "Date unknown"
                )}
              </span>
              {m.variant && <small>{m.variant}</small>}
              <small>
                {m.source_kind === "community"
                  ? "Community observation"
                  : m.source_kind === "retailer"
                    ? "Retailer source"
                    : m.source_kind === "public_mention"
                      ? "Public mention"
                      : "Brand stockist"}
              </small>
            </div>
          </div>
        );
      })}
      {store.phone && (
        <a
          className="text-button"
          href={`tel:${store.phone.replace(/[^+\d]/g, "")}`}
        >
          Call {store.phone}
        </a>
      )}
      <p className="callout">
        <Leaf size={16} />A sighting is a helpful tip. Availability can change
        before you arrive.
      </p>
      <details className="history">
        <summary>
          Sighting history <ChevronDown size={15} />
        </summary>
        {historyLoading && (
          <p role="status" className="muted">
            Loading this shop’s history…
          </p>
        )}
        {historyError && (
          <div role="alert" className="form-error">
            <p>{historyError}</p>
            <button className="text-button" onClick={onRetryHistory}>
              Retry history
            </button>
          </div>
        )}
        {!historyLoading && !historyError && !history.length && (
          <p className="muted">
            No shareable product observations are available at this branch yet.
          </p>
        )}
        {history.map((e) => (
          <article key={e.id}>
            <strong>
              {catalog.find((p) => p.id === e.product_id)?.name ??
                "Brand mention"}
            </strong>
            <p>
              {e.outcome === "saw_it"
                ? "Saw it"
                : e.outcome === "couldnt_find"
                  ? "Couldn’t find it"
                  : "Staff said unavailable"}{" "}
              ·{" "}
              {e.observed_at ? (
                <time
                  dateTime={e.observed_at}
                  title={new Date(e.observed_at).toLocaleString()}
                >
                  {new Date(e.observed_at).toLocaleString()}
                </time>
              ) : (
                "Date unknown"
              )}
            </p>
            {e.note && <p>{e.note}</p>}
            {e.source_url && (
              <a href={e.source_url} target="_blank" rel="noreferrer">
                View source <ExternalLink size={12} />
              </a>
            )}
            {e.photo_path && (
              <button
                className="text-button"
                onClick={async () => {
                  if (demo) return;
                  setPhotoError("");
                  try {
                    await preview(e.id);
                  } catch (error) {
                    if ((error as Error).name !== "AbortError")
                      setPhotoError((error as Error).message);
                  }
                }}
              >
                View shelf photo
              </button>
            )}
            <div>
              <button onClick={() => onReport(e.id)}>Report</button>
              {e.contributor_id && (
                <button onClick={() => onBlock(e.contributor_id!)}>
                  Block contributor
                </button>
              )}
            </div>
          </article>
        ))}
      </details>
      {photoError && (
        <p role="alert" className="form-error">
          {photoError}
        </p>
      )}
      {photo && (
        <div>
          <img
            className="shelf-photo"
            src={photo}
            alt="Reviewed shelf sighting"
            onError={() => {
              clear();
              setPhotoError("This photo could not be displayed.");
            }}
          />
          <button className="text-button" onClick={() => clear()}>
            Close photo
          </button>
        </div>
      )}
      {store.website && (
        <a
          className="text-button"
          href={store.website}
          target="_blank"
          rel="noreferrer"
        >
          Visit shop website <ExternalLink size={14} />
        </a>
      )}
    </div>
  );
}
